import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Ruler, Info } from 'lucide-react';
import { toast } from 'sonner';

const empty = { code: '', name: '', symbol: '', base_unit_code: null, conversion_factor: 1, active: true, sort_order: 100, description: '' };

export default function Units() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/units'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
    const openEdit = (u) => {
        setEditing(u);
        setForm({
            code: u.code, name: u.name, symbol: u.symbol || '',
            base_unit_code: u.base_unit_code || null,
            conversion_factor: u.conversion_factor || 1,
            active: u.active !== false, sort_order: u.sort_order || 100,
            description: u.description || '',
        });
        setDialog(true);
    };

    const save = async () => {
        try {
            const body = { ...form };
            body.code = (body.code || '').trim().toLowerCase();
            if (body.base_unit_code === 'none' || body.base_unit_code === '') body.base_unit_code = null;
            if (!body.base_unit_code) body.conversion_factor = 1;
            body.conversion_factor = parseFloat(body.conversion_factor) || 1;
            if (editing) await api.patch(`/units/${editing.code}`, body);
            else await api.post('/units', body);
            toast.success('Saved'); setDialog(false); load();
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };
    const doDelete = async (u) => {
        if (!window.confirm(`Delete unit "${u.code}"? Products using it must be reassigned first.`)) return;
        try { await api.delete(`/units/${u.code}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    const baseUnits = items.filter(x => x.is_base && (!editing || x.code !== editing.code));

    return (
        <AppShell pageTitle="Units of Measure" pageActions={
            <Button onClick={openNew} data-testid="add-unit-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Unit
            </Button>
        }>
            <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 mb-4 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 text-blue-700 shrink-0" />
                <div className="text-xs text-blue-900">
                    A <strong>base unit</strong> (e.g. <code>pcs</code>, <code>kg</code>, <code>l</code>) has no parent. All other units convert to a base via a factor — e.g. 1 <code>dozen</code> = 12 <code>pcs</code>, 1 <code>bag</code> = 25 <code>kg</code>.
                    Inventory is stored in the base unit; sales in any linked unit are auto-converted.
                </div>
            </div>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Ruler} title="No units yet" description="Add units of measure to price/stock your products." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="units-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Code</th>
                                <th className="text-left font-medium">Name</th>
                                <th className="text-left font-medium">Symbol</th>
                                <th className="text-left font-medium">Base Unit</th>
                                <th className="text-right font-medium">Conversion</th>
                                <th className="text-left font-medium">Type</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(u => (
                                <tr key={u.code} className="border-b hover:bg-neutral-50" data-testid={`unit-row-${u.code}`}>
                                    <td className="p-3 font-mono text-xs">{u.code}</td>
                                    <td className="font-medium">{u.name}</td>
                                    <td>{u.symbol || '—'}</td>
                                    <td className="font-mono text-xs">{u.base_unit_code || <span className="text-neutral-400">—</span>}</td>
                                    <td className="text-right tabular-nums">{u.is_base ? '—' : `× ${u.conversion_factor}`}</td>
                                    <td>{u.is_base ? <span className="text-blue-700 text-xs font-medium">BASE</span> : <span className="text-neutral-500 text-xs">derived</span>}</td>
                                    <td>{u.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`unit-edit-${u.code}`}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(u)} data-testid={`unit-delete-${u.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? `Edit Unit: ${editing.code}` : 'New Unit'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Code *</Label>
                                <Input value={form.code} disabled={!!editing} onChange={e => setForm({ ...form, code: e.target.value.toLowerCase() })} placeholder="e.g. dozen" data-testid="unit-code-input" />
                                {editing && <div className="text-xs text-neutral-500 mt-1">Code is immutable</div>}
                            </div>
                            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dozen" data-testid="unit-name-input" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Symbol</Label><Input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value })} placeholder="e.g. dz" /></div>
                            <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} /></div>
                        </div>
                        <div>
                            <Label>Base Unit</Label>
                            <Select value={form.base_unit_code || 'none'} onValueChange={v => setForm({ ...form, base_unit_code: v === 'none' ? null : v })}>
                                <SelectTrigger data-testid="unit-base-select"><SelectValue placeholder="None (this IS a base unit)" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— None (this IS a base unit) —</SelectItem>
                                    {baseUnits.map(b => (
                                        <SelectItem key={b.code} value={b.code}>{b.code} — {b.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {form.base_unit_code && (
                            <div>
                                <Label>Conversion Factor</Label>
                                <Input type="number" step="0.001" value={form.conversion_factor} onChange={e => setForm({ ...form, conversion_factor: e.target.value })} data-testid="unit-factor-input" />
                                <div className="text-xs text-neutral-500 mt-1">
                                    1 <span className="font-mono">{form.code || 'unit'}</span> = <span className="font-mono">{form.conversion_factor || 1}</span> × <span className="font-mono">{form.base_unit_code}</span>
                                </div>
                            </div>
                        )}
                        <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-unit-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
